// FAT-10: total × fora da amostra (corte fixo) × walk-forward, lado a lado. Estados: vazio (job não rodou),
// insuficiente (histórico curto para separar ajuste e teste) e completo. null nunca vira 0,00%.
import React from 'react';
import './EstatisticasHonestas.css';
import './ForaDaAmostra.css';
import { formatPercentOuIndisponivel, formatRazao } from '../utils/backtestRisk';

const AVISO = 'Resultado passado não garante resultado futuro. O FatCat não executa ordens.';
const SEM = 'sem resultado';

const formatData = data => (data ? String(data).slice(0, 10).split('-').reverse().join('/') : '—');
const formatPf = r => (r.profitFactorInfinite ? '∞ (sem perdas)' : formatRazao(r.profitFactor));
const formatTaxa = valor => (valor == null || !Number.isFinite(Number(valor))
  ? 'indisponível' : `${Number(valor).toFixed(1).replace('.', ',')}%`);

const LINHAS = [
  ['Trades', r => (r.tradesTotal == null ? 'indisponível' : String(r.tradesTotal))],
  ['Expectância líquida', r => formatPercentOuIndisponivel(r.netExpectancyPct)],
  ['Taxa de acerto', r => formatTaxa(r.winRate)],
  ['Profit factor', formatPf],
  ['Retorno líquido', r => formatPercentOuIndisponivel(r.retornoLiquidoPct ?? r.estatisticasHonestas?.retornoLiquidoPct)],
  ['Drawdown máx.', r => formatPercentOuIndisponivel(r.maxDrawdownPct)],
  ['Sharpe anualizado', r => formatRazao(r.estatisticasHonestas?.sharpe)],
];

export default function ForaDaAmostra({ backtest, testId = 'fora-da-amostra' }) {
  const f = backtest?.foraDaAmostra;
  if (!f) {
    return (
      <section className="eh-panel eh-empty" data-testid={`${testId}-vazio`}>
        <h4>Total × fora da amostra</h4>
        <p>Sem resultado fora da amostra: execute o job de backtests para separar ajuste e teste.</p>
      </section>
    );
  }
  if (f.amostraInsuficiente) {
    return (
      <section className="eh-panel eh-insufficient" data-testid={`${testId}-insuficiente`}>
        <h4>Total × fora da amostra</h4>
        <p role="note" className="eh-warning">
          Amostra insuficiente: o histórico não tem os {(f.ajustePregoes ?? 0) + (f.testePregoes ?? 0)} pregões
          de uma janela de ajuste ({f.ajustePregoes ?? '—'}) mais teste ({f.testePregoes ?? '—'}). Só o resultado
          total está disponível, sem verificação fora da amostra.
        </p>
        <p className="eh-disclaimer">{AVISO}</p>
      </section>
    );
  }
  const wf = f.walkForward || {};
  const colunas = [
    { titulo: 'Total', periodo: [backtest.periodStart, backtest.periodEnd], resultado: backtest },
    {
      titulo: `Fora da amostra (últimos ${f.foraDaAmostraPct ?? '—'}%)`,
      periodo: [f.corteFixo?.teste?.inicio, f.corteFixo?.teste?.fim],
      resultado: f.corteFixo?.resultado || null,
    },
    {
      titulo: `Walk-forward (${wf.janelasTotal ?? 0} janelas)`,
      periodo: [wf.janelas?.[0]?.teste?.inicio, wf.janelas?.at(-1)?.teste?.fim],
      resultado: wf.agregado || null,
    },
  ];
  return (
    <section className="eh-panel" data-testid={testId}>
      <h4>Total × fora da amostra</h4>
      <table className="fda-table">
        <thead>
          <tr>
            <th scope="col">Métrica</th>
            {colunas.map(c => (
              <th scope="col" key={c.titulo}>
                {c.titulo}
                <small>{formatData(c.periodo[0])} a {formatData(c.periodo[1])}</small>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {LINHAS.map(([rotulo, valor]) => (
            <tr key={rotulo}>
              <th scope="row">{rotulo}</th>
              {colunas.map(c => <td key={c.titulo}>{c.resultado ? valor(c.resultado) : SEM}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="eh-help fda-nota">
        Ajuste de {f.ajustePregoes} pregões e teste de {f.testePregoes}, avançando {f.passoPregoes} por janela.
        Parâmetros fixos da estratégia: nada é escolhido olhando o período de teste. Cada trade conta na janela
        da sua data de saída; os que entraram antes do teste ficam de fora
        ({wf.tradesCruzandoCorte ?? 0} no walk-forward). "{SEM}" = poucos trades no período.
      </p>
      <p className="eh-disclaimer">{AVISO}</p>
    </section>
  );
}
